import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, Loader2, Plus, RefreshCw, Save, ToggleLeft, ToggleRight, Trash2, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_API_URL}/api`;
const BASE_PLAN_ORDER = ["free", "starter", "pro", "business"];
const MAX_MAIN_PLANS_FALLBACK = 10;
const MEDIA_ORDER = ["image", "video", "audio"];
const REPORT_LANGUAGE_ORDER = ["id", "en"];
const DEFAULT_PRICING_VISIBILITY = {
  section_quota: true,
  section_detect: true,
  section_report: true,
  section_storage: true,
  section_support: true,
  row_monthly_limit: true,
  row_daily_limit: true,
  row_file_limit: true,
  row_cooldown: true,
  row_max_video_duration: true,
  row_max_audio_duration: true,
  row_detect_image: true,
  row_detect_video: true,
  row_detect_audio: true,
  row_report_score: true,
  row_report_ai_explain: true,
  row_report_languages: true,
  row_report_technical: true,
  row_report_pdf: true,
  row_storage_history: true,
  row_storage_upload: true,
  row_storage_grace_days: true,
  row_storage_credits: true,
  row_support_center: true,
  row_support_email: true,
  row_support_api: true,
};
const SECTION_ROW_VISIBILITY_MAP = {
  section_quota: [
    "row_monthly_limit",
    "row_daily_limit",
    "row_file_limit",
    "row_cooldown",
    "row_max_video_duration",
    "row_max_audio_duration",
  ],
  section_detect: ["row_detect_image", "row_detect_video", "row_detect_audio"],
  section_report: ["row_report_score", "row_report_ai_explain", "row_report_languages", "row_report_technical", "row_report_pdf"],
  section_storage: ["row_storage_history", "row_storage_upload", "row_storage_grace_days", "row_storage_credits"],
  section_support: ["row_support_center", "row_support_email", "row_support_api"],
};
const ROW_SECTION_MAP = Object.entries(SECTION_ROW_VISIBILITY_MAP).reduce((acc, [sectionKey, rows]) => {
  rows.forEach((rowKey) => {
    acc[rowKey] = sectionKey;
  });
  return acc;
}, {});

const DEFAULT_MEDIA_ACCESS = { image: true, video: true, audio: true };
const normalizeReportLanguages = (rawValue, fallbackHasEnglish = true) => {
  const incoming = Array.isArray(rawValue)
    ? rawValue
    : typeof rawValue === "string"
      ? rawValue.split(",")
      : [];
  const normalized = [];
  incoming.forEach((lang) => {
    const value = String(lang || "").trim().toLowerCase();
    if (REPORT_LANGUAGE_ORDER.includes(value) && !normalized.includes(value)) {
      normalized.push(value);
    }
  });
  if (!normalized.length) {
    normalized.push("id");
    if (fallbackHasEnglish) normalized.push("en");
  }
  if (!normalized.includes("id")) normalized.unshift("id");
  return REPORT_LANGUAGE_ORDER.filter((lang) => normalized.includes(lang));
};

const DEFAULT_FEATURE_TEMPLATE = {
  report_score: true,
  ai_explanation_level: "basic",
  report_languages: ["id", "en"],
  report_bilingual: true,
  report_technical: true,
  report_pdf_download: true,
  storage_history: true,
  storage_file_upload: true,
  storage_grace_period_days: 7,
  credits_topup_enabled: true,
  support_help_center: true,
  support_email_sla_hours: 48,
  support_api_access: false,
};
const FREE_FEATURE_TEMPLATE = {
  ...DEFAULT_FEATURE_TEMPLATE,
  report_languages: ["id"],
  report_bilingual: false,
  storage_grace_period_days: 0,
  support_email_sla_hours: 0,
  support_api_access: false,
};

const featureTemplateForPlan = (planId) => (planId === "free" ? FREE_FEATURE_TEMPLATE : DEFAULT_FEATURE_TEMPLATE);
const normalizeFeatureDraft = (planId, rawFeature = {}) => {
  const base = featureTemplateForPlan(planId);
  const merged = { ...base, ...(rawFeature || {}) };
  const languages = normalizeReportLanguages(
    merged.report_languages,
    merged.report_bilingual !== false && planId !== "free",
  );
  merged.report_languages = languages;
  merged.report_bilingual = languages.includes("en");
  return merged;
};
const normalizePricingVisibility = (rawValue) => {
  const source = rawValue && typeof rawValue === "object" ? rawValue : {};
  const normalized = Object.keys(DEFAULT_PRICING_VISIBILITY).reduce((acc, key) => {
    acc[key] = source[key] !== undefined ? source[key] !== false : DEFAULT_PRICING_VISIBILITY[key];
    return acc;
  }, {});
  Object.entries(SECTION_ROW_VISIBILITY_MAP).forEach(([sectionKey, rowKeys]) => {
    if (normalized[sectionKey] === false) {
      rowKeys.forEach((rowKey) => {
        normalized[rowKey] = false;
      });
    }
  });
  return normalized;
};

const FEATURE_GROUPS = [
  {
    titleKey: "pricing.sec.report",
    sectionVisibilityKey: "section_report",
    rows: [
      { key: "report_score", type: "boolean", labelKey: "pricing.feat.report.score", visibilityKey: "row_report_score" },
      { key: "ai_explanation_level", type: "select", labelKey: "pricing.compare.row.aiExplain", visibilityKey: "row_report_ai_explain" },
      { key: "report_languages", type: "languages", labelKey: "pricing.compare.row.reportLanguages", visibilityKey: "row_report_languages" },
      { key: "report_technical", type: "boolean", labelKey: "pricing.feat.report.technical", visibilityKey: "row_report_technical" },
      { key: "report_pdf_download", type: "boolean", labelKey: "pricing.feat.report.pdf", visibilityKey: "row_report_pdf" },
    ],
  },
  {
    titleKey: "pricing.sec.storage",
    sectionVisibilityKey: "section_storage",
    rows: [
      { key: "storage_history", type: "boolean", labelKey: "pricing.feat.storage.history", visibilityKey: "row_storage_history" },
      { key: "storage_file_upload", type: "boolean", labelKey: "admin.packages.feature.storageUpload", visibilityKey: "row_storage_upload" },
      { key: "storage_grace_period_days", type: "number", labelKey: "admin.packages.feature.graceDays", min: 0, visibilityKey: "row_storage_grace_days" },
      { key: "credits_topup_enabled", type: "boolean", labelKey: "pricing.feat.storage.credits", visibilityKey: "row_storage_credits" },
    ],
  },
  {
    titleKey: "pricing.sec.support",
    sectionVisibilityKey: "section_support",
    rows: [
      { key: "support_help_center", type: "boolean", labelKey: "pricing.feat.support.center", visibilityKey: "row_support_center" },
      { key: "support_email_sla_hours", type: "number", labelKey: "admin.packages.feature.supportSlaHours", min: 0, visibilityKey: "row_support_email" },
      { key: "support_api_access", type: "boolean", labelKey: "pricing.feat.support.api", visibilityKey: "row_support_api" },
    ],
  },
];

const PLAN_FIELD_ROWS = [
  { key: "display_name", type: "text", labelKey: "admin.packages.field.name" },
  { key: "status", type: "status", labelKey: "admin.packages.field.status" },
  { key: "price_idr", type: "number", labelKey: "admin.packages.field.price", min: 0 },
  { key: "limit", type: "number", labelKey: "admin.packages.field.monthlyLimit", min: 1, visibilityKey: "row_monthly_limit" },
  { key: "daily_limit", type: "number", labelKey: "admin.packages.field.dailyLimit", min: 1, visibilityKey: "row_daily_limit" },
  { key: "file_limit_mb", type: "number", labelKey: "admin.packages.field.fileLimit", min: 1, visibilityKey: "row_file_limit" },
  { key: "cooldown_s", type: "number", labelKey: "admin.packages.field.cooldown", min: 0, visibilityKey: "row_cooldown" },
  { key: "max_video_duration_s", type: "number", labelKey: "admin.packages.field.maxVideoDuration", min: 0, visibilityKey: "row_max_video_duration" },
  { key: "max_audio_duration_s", type: "number", labelKey: "admin.packages.field.maxAudioDuration", min: 0, visibilityKey: "row_max_audio_duration" },
];

const formatPrice = (value) => {
  const amount = Number(value || 0);
  if (amount <= 0) return "Gratis";
  return `Rp ${amount.toLocaleString("id-ID")} /bulan`;
};

const slugifyId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .slice(0, 30);

const buildDefaultPlanDraft = (planId, index = 0) => {
  const isBase = BASE_PLAN_ORDER.includes(planId);
  const basePrices = { free: 0, starter: 79000, pro: 199000, business: 499000 };
  const baseLimits = { free: 3, starter: 25, pro: 80, business: 250 };
  const baseDaily = { free: 1, starter: 5, pro: 15, business: 40 };
  const baseFile = { free: 50, starter: 200, pro: 500, business: 1024 };
  const baseCooldown = { free: 60, starter: 30, pro: 15, business: 5 };
  return {
    display_name: isBase ? String(planId).charAt(0).toUpperCase() + String(planId).slice(1) : "",
    is_active: isBase,
    is_public: isBase,
    is_popular: planId === "pro",
    price_idr: isBase ? Number(basePrices[planId] || 0) : 0,
    limit: Number(baseLimits[planId] || 25),
    daily_limit: Number(baseDaily[planId] || 5),
    file_limit_mb: Number(baseFile[planId] || 200),
    cooldown_s: Number(baseCooldown[planId] || 30),
    max_video_duration_s: 0,
    max_audio_duration_s: 0,
    sort_order: index,
  };
};

export default function AdminPackages() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planDrafts, setPlanDrafts] = useState({});
  const [planMediaAccess, setPlanMediaAccess] = useState({});
  const [planFeatures, setPlanFeatures] = useState({});
  const [pricingVisibility, setPricingVisibility] = useState(DEFAULT_PRICING_VISIBILITY);
  const [creditPackages, setCreditPackages] = useState([]);
  const [maxMainPlans, setMaxMainPlans] = useState(MAX_MAIN_PLANS_FALLBACK);
  const [annualBillingEnabled, setAnnualBillingEnabled] = useState(false);
  const [annualDiscountMode, setAnnualDiscountMode] = useState("percent");
  const [annualDiscountValue, setAnnualDiscountValue] = useState("0");
  const [annualDiscountMaxNominal, setAnnualDiscountMaxNominal] = useState(0);
  const planOrder = useMemo(() => {
    const rows = Object.entries(planDrafts || {})
      .map(([id, cfg]) => ({ id, sort: Number(cfg?.sort_order ?? 9999) }))
      .sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id))
      .map((r) => r.id);
    const ordered = [];
    BASE_PLAN_ORDER.forEach((planId) => {
      if (!ordered.includes(planId)) ordered.push(planId);
    });
    rows.forEach((planId) => {
      if (!ordered.includes(planId)) ordered.push(planId);
    });
    return ordered.slice(0, maxMainPlans);
  }, [planDrafts, maxMainPlans]);


  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/plans/config`, { withCredentials: true });
      const fetchedPlans = res.data.plans || [];
      const fetchedMaxMainPlans = Number(res.data.max_main_plans || MAX_MAIN_PLANS_FALLBACK);
      const fallbackOverrides = fetchedPlans.reduce((acc, p, idx) => {
        acc[p.id] = {
          display_name: p.name || p.id,
          is_active: p.is_active !== false,
          is_public: p.is_public !== false,
          is_popular: p.is_popular === true,
          price_idr: p.price_idr,
          limit: p.limit,
          daily_limit: p.daily_limit,
          file_limit_mb: p.file_limit_mb,
          cooldown_s: p.cooldown_s,
          max_video_duration_s: p.max_video_duration_s ?? 0,
          max_audio_duration_s: p.max_audio_duration_s ?? 0,
          sort_order: Number(p.sort_order ?? idx),
        };
        return acc;
      }, {});
      const rawDrafts = (res.data.plan_overrides && typeof res.data.plan_overrides === "object")
        ? res.data.plan_overrides
        : fallbackOverrides;
      const sortedIds = Object.entries(rawDrafts)
        .sort((a, b) => Number(a?.[1]?.sort_order ?? 9999) - Number(b?.[1]?.sort_order ?? 9999))
        .map(([id]) => id);
      const normalizedIds = Array.from(new Set([...BASE_PLAN_ORDER, ...sortedIds])).slice(0, fetchedMaxMainPlans);
      const normalizedDrafts = {};
      normalizedIds.forEach((planId, idx) => {
        normalizedDrafts[planId] = {
          ...buildDefaultPlanDraft(planId, idx),
          ...(rawDrafts?.[planId] || {}),
          sort_order: idx,
        };
      });
      const rawMediaAccess = (res.data.plan_media_access && typeof res.data.plan_media_access === "object")
        ? res.data.plan_media_access
        : {};
      const normalizedMediaAccess = {};
      normalizedIds.forEach((planId) => {
        normalizedMediaAccess[planId] = {
          ...DEFAULT_MEDIA_ACCESS,
          ...(rawMediaAccess?.[planId] || {}),
        };
      });
      const rawFeatures = (res.data.plan_feature_overrides && typeof res.data.plan_feature_overrides === "object")
        ? res.data.plan_feature_overrides
        : {};
      const normalizedFeatures = {};
      normalizedIds.forEach((planId) => {
        normalizedFeatures[planId] = normalizeFeatureDraft(planId, rawFeatures?.[planId] || {});
      });
      setMaxMainPlans(Math.min(Math.max(4, fetchedMaxMainPlans), MAX_MAIN_PLANS_FALLBACK));
      setPlanDrafts(normalizedDrafts);
      setPlanMediaAccess(normalizedMediaAccess);
      setPlanFeatures(normalizedFeatures);
      setPricingVisibility(normalizePricingVisibility(res.data.pricing_visibility));
      const normalizedCreditPackages = Array.isArray(res.data.credit_packages)
        ? res.data.credit_packages.map((pack) => ({
            ...pack,
            is_active: pack?.is_active !== false,
            is_public: pack?.is_public !== false,
            is_most_used: pack?.is_most_used === true,
          }))
        : [];
      if (normalizedCreditPackages.length > 0 && !normalizedCreditPackages.some((pack) => pack.is_most_used)) {
        const firstVisible = normalizedCreditPackages.find((pack) => pack.is_active !== false && pack.is_public !== false);
        if (firstVisible) firstVisible.is_most_used = true;
      }
      setCreditPackages(normalizedCreditPackages);
      setAnnualBillingEnabled(!!res.data.annual_billing_enabled);
      setAnnualDiscountMode(res.data.annual_discount_mode || "percent");
      setAnnualDiscountValue(String(res.data.annual_discount_value ?? 0));
      setAnnualDiscountMaxNominal(Number(res.data.annual_discount_max_nominal ?? 0));
    } catch {
      toast.error(t("admin.packages.loadFail"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updatePlanField = (planId, field, value) => {
    setPlanDrafts((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      const parsed = Number.parseInt(value || "0", 10);
      next[planId][field] = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
      if (planId === "free" && field === "price_idr") next[planId][field] = 0;
      return next;
    });
  };

  const updatePlanTextField = (planId, field, value) => {
    setPlanDrafts((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      next[planId][field] = String(value || "").slice(0, 30);
      return next;
    });
  };

  const togglePlanActive = (planId) => {
    if (planId === "free") return;
    setPlanDrafts((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      const nextActive = !next[planId].is_active;
      next[planId].is_active = nextActive;
      if (!nextActive) {
        next[planId].is_popular = false;
      }
      if (nextActive && Number(next[planId].price_idr || 0) <= 0) {
        next[planId].price_idr = 1000;
      }
      if (planId === "free") {
        next[planId].is_active = true;
      }
      return next;
    });
  };

  const togglePlanPublic = (planId) => {
    if (planId === "free") return;
    setPlanDrafts((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      const nextPublic = !next[planId].is_public;
      next[planId].is_public = nextPublic;
      if (!nextPublic) {
        next[planId].is_popular = false;
      }
      if (nextPublic && Number(next[planId].price_idr || 0) <= 0) {
        next[planId].price_idr = 1000;
      }
      return next;
    });
  };

  const togglePlanPopular = (planId) => {
    if (planId === "free") return;
    setPlanDrafts((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      const nextPopular = !next[planId].is_popular;
      Object.keys(next).forEach((pid) => {
        next[pid] = { ...(next[pid] || {}) };
        next[pid].is_popular = false;
      });
      if (nextPopular) {
        next[planId].is_popular = true;
        next[planId].is_active = true;
        next[planId].is_public = true;
        if (Number(next[planId].price_idr || 0) <= 0) {
          next[planId].price_idr = 1000;
        }
      }
      return next;
    });
  };

  const addMainPlan = () => {
    const ids = Object.keys(planDrafts || {});
    if (ids.length >= maxMainPlans) {
      toast.error(t("admin.packages.main.maxTen", { max: maxMainPlans }));
      return;
    }
    let idx = 1;
    let nextId = `plan_${idx}`;
    while (ids.includes(nextId)) {
      idx += 1;
      nextId = `plan_${idx}`;
    }
    setPlanDrafts((prev) => ({
      ...prev,
      [nextId]: buildDefaultPlanDraft(nextId, ids.length),
    }));
    setPlanMediaAccess((prev) => ({
      ...prev,
      [nextId]: { ...DEFAULT_MEDIA_ACCESS },
    }));
    setPlanFeatures((prev) => ({
      ...prev,
      [nextId]: normalizeFeatureDraft(nextId),
    }));
  };

  const removeMainPlan = (planId) => {
    if (planId === "free") return;
    setPlanDrafts((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
    setPlanMediaAccess((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
    setPlanFeatures((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
  };

  const toggleMedia = (planId, mediaType) => {
    setPlanMediaAccess((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      next[planId][mediaType] = !next[planId][mediaType];
      return next;
    });
  };

  const toggleFeature = (planId, key) => {
    setPlanFeatures((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      next[planId][key] = !next[planId][key];
      return next;
    });
  };
  const togglePricingVisibility = (key) => {
    setPricingVisibility((prev) => ({
      ...prev,
      [key]: !(prev?.[key] !== false),
    }));
  };
  const toggleSectionPricingVisibility = (sectionKey) => {
    setPricingVisibility((prev) => {
      const nextVisible = !(prev?.[sectionKey] !== false);
      const next = { ...prev, [sectionKey]: nextVisible };
      (SECTION_ROW_VISIBILITY_MAP[sectionKey] || []).forEach((rowKey) => {
        next[rowKey] = nextVisible;
      });
      return next;
    });
  };

  const toggleFeatureLanguage = (planId, lang) => {
    if (lang === "id") return;
    setPlanFeatures((prev) => {
      const next = { ...prev };
      const draft = normalizeFeatureDraft(planId, next[planId] || {});
      const current = [...draft.report_languages];
      const hasLang = current.includes(lang);
      const updated = hasLang ? current.filter((item) => item !== lang) : [...current, lang];
      draft.report_languages = normalizeReportLanguages(updated, true);
      draft.report_bilingual = draft.report_languages.includes("en");
      next[planId] = draft;
      return next;
    });
  };

  const updateFeatureNumber = (planId, key, value, min = 0) => {
    setPlanFeatures((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      const parsed = Number.parseInt(value || "0", 10);
      next[planId][key] = Number.isNaN(parsed) ? min : Math.max(min, parsed);
      return next;
    });
  };

  const updateFeatureSelect = (planId, key, value) => {
    setPlanFeatures((prev) => {
      const next = { ...prev };
      next[planId] = { ...(next[planId] || {}) };
      next[planId][key] = value;
      return next;
    });
  };

  const addCreditPackage = () => {
    setCreditPackages((prev) => {
      if (prev.length >= 10) return prev;
      const baseIndex = prev.length + 1;
      const nextId = slugifyId(`pack_${baseIndex}`) || `pack_${baseIndex}`;
      return [
        ...prev,
        {
          id: nextId,
          label: `${baseIndex * 10} Kredit`,
          credits: baseIndex * 10,
          price_idr: Math.max(1000, baseIndex * 10000),
          is_active: true,
          is_public: true,
          is_most_used: !prev.some((item) => item?.is_most_used),
        },
      ];
    });
  };

  const removeCreditPackage = (index) => {
    setCreditPackages((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCreditPackage = (index, key, value) => {
    setCreditPackages((prev) => {
      let next = prev.map((item, i) => {
        if (i !== index) return item;
        if (key === "id") return { ...item, id: slugifyId(value) };
        if (key === "credits") return { ...item, credits: Math.max(1, Number.parseInt(value || "1", 10) || 1) };
        if (key === "price_idr") return { ...item, price_idr: Math.max(1000, Number.parseInt(value || "1000", 10) || 1000) };
        if (key === "label") return { ...item, label: String(value || "").slice(0, 60) };
        if (key === "is_active") {
          const nextActive = !!value;
          return {
            ...item,
            is_active: nextActive,
            is_public: nextActive ? item.is_public !== false : false,
            is_most_used: nextActive ? item.is_most_used === true : false,
          };
        }
        if (key === "is_public") {
          const nextPublic = !!value;
          return {
            ...item,
            is_public: nextPublic,
            is_active: item.is_active !== false || nextPublic,
            is_most_used: nextPublic ? item.is_most_used === true : false,
          };
        }
        if (key === "is_most_used") {
          const nextMostUsed = !!value;
          return { ...item, is_most_used: nextMostUsed, is_active: true, is_public: true };
        }
        return item;
      });
      if (key === "is_most_used" && !!value) {
        next = next.map((item, i) => ({ ...item, is_most_used: i === index }));
      }
      const visibleMostUsed = next.filter((item) => item.is_active !== false && item.is_public !== false && item.is_most_used);
      if (visibleMostUsed.length > 1) {
        const keepId = visibleMostUsed[0].id;
        next = next.map((item) => ({ ...item, is_most_used: item.id === keepId }));
      }
      if (!next.some((item) => item.is_active !== false && item.is_public !== false && item.is_most_used)) {
        const firstVisible = next.find((item) => item.is_active !== false && item.is_public !== false);
        if (firstVisible) {
          next = next.map((item) => ({ ...item, is_most_used: item.id === firstVisible.id }));
        }
      }
      return next;
    });
  };

  const toggleCreditPackageFlag = (index, key) => {
    const current = creditPackages?.[index];
    if (!current) return;
    const nextValue = !Boolean(current?.[key]);
    updateCreditPackage(index, key, nextValue);
  };

  const annualPreview = useMemo(() => {
    const parsed = Number.parseInt(annualDiscountValue || "0", 10);
    const normalized = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    if (annualDiscountMode === "percent") return `${normalized}%`;
    return `Rp ${normalized.toLocaleString("id-ID")}`;
  }, [annualDiscountMode, annualDiscountValue]);
  const matrixTableMinWidth = useMemo(() => Math.max(1260, 260 + planOrder.length * 230 + 170), [planOrder.length]);

  const saveConfig = async () => {
    const parsedAnnual = Number.parseInt(annualDiscountValue || "0", 10);
    const annualValue = Number.isNaN(parsedAnnual) ? 0 : Math.max(0, parsedAnnual);
    const annualMax = annualDiscountMode === "percent" ? 90 : annualDiscountMaxNominal;
    if (annualValue > annualMax) {
      toast.error(
        annualDiscountMode === "percent"
          ? t("admin.system.discountPercentMax", { max: 90 })
          : t("admin.system.discountNominalMax", { max: annualDiscountMaxNominal.toLocaleString("id-ID") }),
      );
      return;
    }
    if (creditPackages.length > 10) {
      toast.error(t("admin.packages.credit.maxTen"));
      return;
    }
    if (planOrder.length > maxMainPlans) {
      toast.error(t("admin.packages.main.maxTen", { max: maxMainPlans }));
      return;
    }

    const normalizedPlanPayload = {};
    planOrder.forEach((planId, idx) => {
      const draft = planDrafts?.[planId] || {};
      normalizedPlanPayload[planId] = {
        ...draft,
        sort_order: idx,
      };
    });
    const normalizedFeaturePayload = {};
    planOrder.forEach((planId) => {
      normalizedFeaturePayload[planId] = normalizeFeatureDraft(planId, planFeatures?.[planId] || {});
    });
    let normalizedCreditPackages = creditPackages.map((pack) => ({
      ...pack,
      is_active: pack?.is_active !== false,
      is_public: pack?.is_active === false ? false : pack?.is_public !== false,
      is_most_used: pack?.is_most_used === true,
    }));
    const visibleMostUsed = normalizedCreditPackages.filter((pack) => pack.is_active && pack.is_public && pack.is_most_used);
    if (visibleMostUsed.length > 1) {
      const keepId = visibleMostUsed[0].id;
      normalizedCreditPackages = normalizedCreditPackages.map((pack) => ({ ...pack, is_most_used: pack.id === keepId }));
    } else if (visibleMostUsed.length === 0) {
      const firstVisible = normalizedCreditPackages.find((pack) => pack.is_active && pack.is_public);
      if (firstVisible) {
        normalizedCreditPackages = normalizedCreditPackages.map((pack) => ({ ...pack, is_most_used: pack.id === firstVisible.id }));
      }
    }

    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/plans/config`,
        {
          plan_overrides: normalizedPlanPayload,
          plan_media_access: planMediaAccess,
          plan_feature_overrides: normalizedFeaturePayload,
          pricing_visibility: normalizePricingVisibility(pricingVisibility),
          credit_packages: normalizedCreditPackages,
          annual_billing_enabled: annualBillingEnabled,
          annual_discount_mode: annualDiscountMode,
          annual_discount_value: annualValue,
        },
        { withCredentials: true },
      );
      toast.success(t("admin.packages.saved"));
      await loadConfig();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.packages.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  const renderVisibilityCell = (visibilityKey, dataTestId, opts = {}) => {
    const isSection = opts.isSection === true;
    const explicitSectionKey = opts.sectionKey || "";
    if (!visibilityKey) {
      return (
        <span className="text-xs text-muted-foreground/50">
          —
        </span>
      );
    }
    const isPublic = pricingVisibility?.[visibilityKey] !== false;
    const parentSectionKey = isSection ? visibilityKey : (explicitSectionKey || ROW_SECTION_MAP[visibilityKey]);
    const isRowDisabled = !isSection && !!parentSectionKey && pricingVisibility?.[parentSectionKey] === false;
    return (
      <button
        type="button"
        onClick={() => {
          if (isRowDisabled) return;
          if (isSection) {
            toggleSectionPricingVisibility(visibilityKey);
            return;
          }
          togglePricingVisibility(visibilityKey);
        }}
        className={`inline-flex items-center justify-center gap-1 h-8 min-w-[110px] rounded-sm border border-border text-xs ${
          isRowDisabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        disabled={isRowDisabled}
        data-testid={dataTestId}
      >
        {isPublic ? <ToggleRight className="w-4 h-4 text-brand-primary" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
        <span>{isPublic ? t("admin.packages.state.public") : t("admin.packages.state.private")}</span>
      </button>
    );
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-packages-page">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("admin.packages.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("admin.packages.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm gap-1.5"
            onClick={loadConfig}
            disabled={loading || saving}
            data-testid="admin-packages-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {t("admin.system.refresh")}
          </Button>
          <Button
            size="sm"
            className="rounded-sm bg-brand-primary text-white gap-1.5"
            onClick={saveConfig}
            disabled={loading || saving}
            data-testid="admin-packages-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {t("common.save")}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-64 rounded-md border border-border bg-card animate-pulse" />
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="rounded-md border border-border bg-card p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-sm font-medium mb-1">{t("admin.packages.annualTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("admin.packages.annualDesc")}</p>
              </div>
              <button
                type="button"
                onClick={() => setAnnualBillingEnabled((v) => !v)}
                className="inline-flex items-center gap-2 text-sm text-brand-primary"
                data-testid="admin-packages-annual-toggle"
              >
                {annualBillingEnabled ? <ToggleRight className="w-8 h-8 text-verdict-real" /> : <ToggleLeft className="w-8 h-8 text-muted-foreground" />}
                <span>{annualBillingEnabled ? t("admin.packages.state.enabled") : t("admin.packages.state.disabled")}</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
              <div>
                <label className="text-sm text-muted-foreground">{t("admin.system.discountType")}</label>
                <select
                  value={annualDiscountMode}
                  onChange={(e) => setAnnualDiscountMode(e.target.value)}
                  disabled={!annualBillingEnabled}
                  className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                >
                  <option value="percent">{t("admin.system.discountPercent")}</option>
                  <option value="nominal">{t("admin.system.discountNominal")}</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">{t("admin.system.discountValue")}</label>
                <input
                  type="number"
                  min="0"
                  max={annualDiscountMode === "percent" ? 90 : annualDiscountMaxNominal}
                  value={annualDiscountValue}
                  onChange={(e) => setAnnualDiscountValue(e.target.value)}
                  disabled={!annualBillingEnabled}
                  className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                  data-testid="admin-packages-annual-discount-value"
                />
                <p className="mt-1 text-sm text-muted-foreground">
                  {annualDiscountMode === "percent"
                    ? t("admin.system.discountPercentMax", { max: 90 })
                    : t("admin.system.discountNominalMax", { max: annualDiscountMaxNominal.toLocaleString("id-ID") })}
                </p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">{t("admin.system.discountPreviewLabel")}</label>
                <input
                  type="text"
                  value={annualPreview}
                  readOnly
                  className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-medium mb-1">{t("admin.packages.planSettingsTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("admin.packages.planSettingsDesc")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("admin.packages.mainPlanPolicy")}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("admin.packages.mainCount", { count: planOrder.length, max: maxMainPlans })}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm gap-1.5"
                onClick={addMainPlan}
                disabled={planOrder.length >= maxMainPlans}
                data-testid="admin-packages-add-main-plan-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                {t("admin.packages.mainAdd")}
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="p-4 border-b border-border/60">
              <h2 className="text-sm font-medium mb-1">{t("admin.packages.matrixTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("admin.packages.matrixDesc")}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: `${matrixTableMinWidth}px` }}>
                <thead className="bg-secondary/40">
                  <tr className="border-b border-border">
                    <th className="sticky left-0 z-10 bg-secondary/40 px-4 py-3 text-left font-mono uppercase tracking-wider text-muted-foreground min-w-[260px]">
                      {t("pricing.compare.col.feature")}
                    </th>
                    {planOrder.map((planId) => {
                      const draft = planDrafts?.[planId] || {};
                      return (
                        <th key={planId} className="px-4 py-3 text-center align-top font-mono uppercase tracking-wider text-muted-foreground min-w-[230px]">
                          <div className="flex items-start justify-between gap-1">
                            <div className="text-left">
                              <div className="text-sm font-semibold normal-case tracking-normal text-foreground">
                                {String(draft.display_name || planId)}
                              </div>
                              <div className="text-[11px] normal-case tracking-normal text-muted-foreground">{planId}</div>
                              <div className="text-[11px] normal-case tracking-normal text-muted-foreground/80 mt-0.5">
                                {formatPrice(draft.price_idr)}
                              </div>
                            </div>
                            {planId !== "free" && !BASE_PLAN_ORDER.includes(planId) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-verdict-fake border-verdict-fake/40 hover:bg-verdict-fake/10"
                                onClick={() => removeMainPlan(planId)}
                                data-testid={`plan-delete-${planId}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </th>
                      );
                    })}
                    <th className="px-4 py-3 text-center align-middle font-mono uppercase tracking-wider text-muted-foreground min-w-[170px]">
                      {t("admin.packages.visibilityPublic")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/60 bg-background/40">
                    <td colSpan={planOrder.length + 1} className="px-4 py-2 font-semibold text-muted-foreground">
                      {t("admin.packages.planSettingsTitle")}
                    </td>
                    <td className="px-4 py-2 text-center align-middle">
                      {renderVisibilityCell("section_quota", "visibility-section-quota", { isSection: true })}
                    </td>
                  </tr>
                  {PLAN_FIELD_ROWS.map((row) => (
                    <tr key={row.key} className="border-b border-border/40">
                      <td className="sticky left-0 z-10 bg-card px-4 py-3 text-muted-foreground align-middle">{t(row.labelKey)}</td>
                      {planOrder.map((planId) => {
                        const draft = planDrafts?.[planId] || {};
                        if (row.type === "text") {
                          return (
                            <td key={`${planId}-${row.key}`} className="px-4 py-3 align-middle">
                              <input
                                type="text"
                                value={String(draft.display_name || "")}
                                onChange={(e) => updatePlanTextField(planId, "display_name", e.target.value)}
                                className="w-full bg-background border border-border rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                data-testid={`plan-name-${planId}`}
                              />
                            </td>
                          );
                        }
                        if (row.type === "status") {
                          return (
                            <td key={`${planId}-${row.key}`} className="px-4 py-3 align-middle">
                              <div className="grid grid-cols-3 gap-2">
                                <button
                                  type="button"
                                  onClick={() => togglePlanActive(planId)}
                                  disabled={planId === "free"}
                                  className={`inline-flex items-center justify-center gap-1 h-8 rounded-sm border border-border text-xs ${planId === "free" ? "opacity-50 cursor-not-allowed" : ""}`}
                                  data-testid={`plan-active-toggle-${planId}`}
                                >
                                  {draft.is_active === false ? <ToggleLeft className="w-4 h-4 text-muted-foreground" /> : <ToggleRight className="w-4 h-4 text-verdict-real" />}
                                  <span>{draft.is_active === false ? t("admin.packages.state.disabled") : t("admin.packages.state.enabled")}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => togglePlanPublic(planId)}
                                  disabled={planId === "free"}
                                  className={`inline-flex items-center justify-center gap-1 h-8 rounded-sm border border-border text-xs ${planId === "free" ? "opacity-50 cursor-not-allowed" : ""}`}
                                  data-testid={`plan-public-toggle-${planId}`}
                                >
                                  {draft.is_public === false ? <ToggleLeft className="w-4 h-4 text-muted-foreground" /> : <ToggleRight className="w-4 h-4 text-brand-primary" />}
                                  <span>{draft.is_public === false ? t("admin.packages.state.private") : t("admin.packages.state.public")}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => togglePlanPopular(planId)}
                                  disabled={planId === "free"}
                                  className={`inline-flex items-center justify-center gap-1 h-8 rounded-sm border border-border text-xs ${planId === "free" ? "opacity-50 cursor-not-allowed" : ""}`}
                                  data-testid={`plan-popular-toggle-${planId}`}
                                >
                                  {draft.is_popular ? <ToggleRight className="w-4 h-4 text-brand-primary" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                                  <span>{t("admin.packages.state.popular")}</span>
                                </button>
                              </div>
                            </td>
                          );
                        }
                        return (
                          <td key={`${planId}-${row.key}`} className="px-4 py-3 align-middle">
                            {(() => {
                              const map = {
                                price_idr: `plan-price-${planId}`,
                                limit: `plan-limit-${planId}`,
                                daily_limit: `plan-daily-${planId}`,
                                file_limit_mb: `plan-file-${planId}`,
                                cooldown_s: `plan-cooldown-${planId}`,
                                max_video_duration_s: `plan-video-duration-${planId}`,
                                max_audio_duration_s: `plan-audio-duration-${planId}`,
                              };
                              const dataTestId = map[row.key] || `plan-${row.key}-${planId}`;
                              return (
                            <input
                              type="number"
                              min={row.min ?? 0}
                              value={Number(draft[row.key] || 0)}
                              disabled={planId === "free" && row.key === "price_idr"}
                              onChange={(e) => updatePlanField(planId, row.key, e.target.value)}
                              className="w-full bg-background border border-border rounded-sm px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                              data-testid={dataTestId}
                            />
                              );
                            })()}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center align-middle">
                        {renderVisibilityCell(row.visibilityKey, `visibility-row-${row.key}`, { sectionKey: "section_quota" })}
                      </td>
                    </tr>
                  ))}

                  <tr className="border-b border-border/60 bg-background/40">
                    <td colSpan={planOrder.length + 1} className="px-4 py-2 font-semibold text-muted-foreground">
                      {t("admin.packages.mediaAccessTitle")}
                    </td>
                    <td className="px-4 py-2 text-center align-middle">
                      {renderVisibilityCell("section_detect", "visibility-section-detect", { isSection: true })}
                    </td>
                  </tr>
                  {MEDIA_ORDER.map((mediaType) => (
                    <tr key={mediaType} className="border-b border-border/40">
                      <td className="sticky left-0 z-10 bg-card px-4 py-3 text-muted-foreground align-middle">{t(`pricing.feat.detect.${mediaType}`)}</td>
                      {planOrder.map((planId) => {
                        const enabled = !!planMediaAccess?.[planId]?.[mediaType];
                        return (
                          <td key={`${planId}-${mediaType}`} className="px-4 py-3 text-center align-middle">
                            <button
                              type="button"
                              onClick={() => toggleMedia(planId, mediaType)}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                                enabled
                                  ? "border-verdict-real/40 bg-verdict-real/10 text-verdict-real"
                                  : "border-border text-muted-foreground hover:text-foreground"
                              }`}
                              data-testid={`plan-media-${planId}-${mediaType}`}
                            >
                              {enabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center align-middle">
                        {renderVisibilityCell(`row_detect_${mediaType}`, `visibility-row-detect-${mediaType}`, { sectionKey: "section_detect" })}
                      </td>
                    </tr>
                  ))}

                  {FEATURE_GROUPS.map((group) => (
                    <GroupRows key={group.titleKey}>
                      <tr className="border-b border-border/60 bg-background/40">
                        <td colSpan={planOrder.length + 1} className="px-4 py-2 font-semibold text-muted-foreground">
                          {t(group.titleKey)}
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          {renderVisibilityCell(group.sectionVisibilityKey, `visibility-${group.sectionVisibilityKey}`, { isSection: true })}
                        </td>
                      </tr>
                      {group.rows.map((row) => (
                        <tr key={row.key} className="border-b border-border/40">
                          <td className="sticky left-0 z-10 bg-card px-4 py-3 text-muted-foreground align-middle">{t(row.labelKey)}</td>
                          {planOrder.map((planId) => {
                            const value = planFeatures?.[planId]?.[row.key];
                            if (row.type === "boolean") {
                              const enabled = !!value;
                              return (
                                <td key={`${planId}-${row.key}`} className="px-4 py-3 text-center align-middle">
                                  <button
                                    type="button"
                                    onClick={() => toggleFeature(planId, row.key)}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                                      enabled
                                        ? "border-verdict-real/40 bg-verdict-real/10 text-verdict-real"
                                        : "border-border text-muted-foreground hover:text-foreground"
                                    }`}
                                    data-testid={`plan-feature-${planId}-${row.key}`}
                                  >
                                    {enabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                  </button>
                                </td>
                              );
                            }
                            if (row.type === "select") {
                              return (
                                <td key={`${planId}-${row.key}`} className="px-4 py-3 text-center align-middle">
                                  <select
                                    value={value || "basic"}
                                    onChange={(e) => updateFeatureSelect(planId, row.key, e.target.value)}
                                    className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                    data-testid={`plan-feature-${planId}-${row.key}`}
                                  >
                                    <option value="none">{t("admin.packages.ai.none")}</option>
                                    <option value="basic">{t("admin.packages.ai.basic")}</option>
                                    <option value="full">{t("admin.packages.ai.full")}</option>
                                  </select>
                                </td>
                              );
                            }
                            if (row.type === "languages") {
                              const languages = normalizeReportLanguages(value, true);
                              return (
                                <td key={`${planId}-${row.key}`} className="px-4 py-3 text-center align-middle">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      type="button"
                                      disabled
                                      className="inline-flex items-center justify-center h-8 min-w-12 px-2 rounded-sm border border-verdict-real/40 bg-verdict-real/10 text-xs text-verdict-real opacity-80 cursor-not-allowed"
                                    >
                                      {t("admin.packages.language.id")}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => toggleFeatureLanguage(planId, "en")}
                                      className={`inline-flex items-center justify-center h-8 min-w-12 px-2 rounded-sm border text-xs transition-colors ${
                                        languages.includes("en")
                                          ? "border-verdict-real/40 bg-verdict-real/10 text-verdict-real"
                                          : "border-border text-muted-foreground hover:text-foreground"
                                      }`}
                                      data-testid={`plan-feature-${planId}-${row.key}-en`}
                                    >
                                      {t("admin.packages.language.en")}
                                    </button>
                                  </div>
                                </td>
                              );
                            }
                            return (
                              <td key={`${planId}-${row.key}`} className="px-4 py-3 text-center align-middle">
                                <input
                                  type="number"
                                  min={row.min ?? 0}
                                  value={Number(value ?? 0)}
                                  onChange={(e) => updateFeatureNumber(planId, row.key, e.target.value, row.min ?? 0)}
                                  className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                  data-testid={`plan-feature-${planId}-${row.key}`}
                                />
                              </td>
                            );
                          })}
                          <td className="px-4 py-3 text-center align-middle">
                            {renderVisibilityCell(row.visibilityKey, `visibility-row-${row.key}`, { sectionKey: group.sectionVisibilityKey })}
                          </td>
                        </tr>
                      ))}
                    </GroupRows>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-medium mb-1">{t("admin.packages.creditTitle")}</h2>
                <p className="text-sm text-muted-foreground">{t("admin.packages.creditDesc")}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm gap-1.5"
                onClick={addCreditPackage}
                disabled={creditPackages.length >= 10}
                data-testid="admin-packages-add-credit-pack-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                {t("admin.packages.creditAdd")}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t("admin.packages.creditCount", { count: creditPackages.length })}
            </p>

            <div className="overflow-x-auto mt-3 rounded-sm border border-border">
              <table className="w-full min-w-[1120px] text-sm">
                <thead className="bg-secondary/40">
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-muted-foreground">ID</th>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-muted-foreground">{t("admin.packages.creditLabel")}</th>
                    <th className="px-3 py-2 text-center font-mono uppercase tracking-wider text-muted-foreground">{t("admin.packages.creditCredits")}</th>
                    <th className="px-3 py-2 text-center font-mono uppercase tracking-wider text-muted-foreground">{t("admin.packages.creditPrice")}</th>
                    <th className="px-3 py-2 text-center font-mono uppercase tracking-wider text-muted-foreground">{t("admin.packages.field.status")}</th>
                    <th className="px-3 py-2 text-center font-mono uppercase tracking-wider text-muted-foreground">{t("admin.packages.creditAction")}</th>
                  </tr>
                </thead>
                <tbody>
                  {creditPackages.map((pack, idx) => (
                    <tr key={`${pack.id}-${idx}`} className="border-b border-border/40 last:border-0">
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="text"
                          value={pack.id || ""}
                          onChange={(e) => updateCreditPackage(idx, "id", e.target.value)}
                          className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                          data-testid={`credit-pack-id-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="text"
                          value={pack.label || ""}
                          onChange={(e) => updateCreditPackage(idx, "label", e.target.value)}
                          className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                          data-testid={`credit-pack-label-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="number"
                          min={1}
                          value={Number(pack.credits || 1)}
                          onChange={(e) => updateCreditPackage(idx, "credits", e.target.value)}
                          className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                          data-testid={`credit-pack-credits-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="number"
                          min={1000}
                          value={Number(pack.price_idr || 1000)}
                          onChange={(e) => updateCreditPackage(idx, "price_idr", e.target.value)}
                          className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                          data-testid={`credit-pack-price-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => toggleCreditPackageFlag(idx, "is_active")}
                            className="inline-flex items-center justify-center gap-1 h-8 rounded-sm border border-border text-xs"
                            data-testid={`credit-pack-active-toggle-${idx}`}
                          >
                            {pack.is_active === false ? <ToggleLeft className="w-4 h-4 text-muted-foreground" /> : <ToggleRight className="w-4 h-4 text-verdict-real" />}
                            <span>{pack.is_active === false ? t("admin.packages.state.disabled") : t("admin.packages.state.enabled")}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleCreditPackageFlag(idx, "is_public")}
                            disabled={pack.is_active === false}
                            className={`inline-flex items-center justify-center gap-1 h-8 rounded-sm border border-border text-xs ${pack.is_active === false ? "opacity-50 cursor-not-allowed" : ""}`}
                            data-testid={`credit-pack-public-toggle-${idx}`}
                          >
                            {pack.is_public === false ? <ToggleLeft className="w-4 h-4 text-muted-foreground" /> : <ToggleRight className="w-4 h-4 text-brand-primary" />}
                            <span>{pack.is_public === false ? t("admin.packages.state.private") : t("admin.packages.state.public")}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleCreditPackageFlag(idx, "is_most_used")}
                            className="inline-flex items-center justify-center gap-1 h-8 rounded-sm border border-border text-xs"
                            data-testid={`credit-pack-most-used-toggle-${idx}`}
                          >
                            {pack.is_most_used ? <ToggleRight className="w-4 h-4 text-brand-primary" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                            <span>{pack.is_most_used ? t("admin.packages.state.mostUsed") : t("admin.packages.state.notMostUsed")}</span>
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center align-middle">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm h-8 px-2 text-verdict-fake border-verdict-fake/40 hover:bg-verdict-fake/10"
                          onClick={() => removeCreditPackage(idx)}
                          disabled={creditPackages.length <= 1}
                          data-testid={`credit-pack-delete-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {creditPackages.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-5 text-center text-sm text-muted-foreground">
                        {t("admin.packages.creditEmpty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function GroupRows({ children }) {
  return <>{children}</>;
}
